import * as React from 'react'

import { observer } from 'mobx-react'

import { useEquipmentResourceStore } from '~/client/src/shared/hooks/useEquipmentResourceStore'
import { useMaterialResourceStore } from '~/client/src/shared/hooks/useMaterialResourceStore'
import { IResourceRecord } from '~/client/src/shared/resources/definitions/types'
import {
  buildResourceControlFieldsMap,
  buildResourceOperatingIntervalsMap,
  buildResourcePermittedCompaniesMap,
  buildResourceWorkflowRestrictionsMap,
} from '~/client/src/shared/utils/resourceRestrictionHelpers'

import SitePermitCreationFormStore from '../WorkflowCreationForm.store'

type ResourceRestrictionsStore = Pick<
  SitePermitCreationFormStore,
  | 'setFreshResourcePermittedCompanies'
  | 'clearFreshResourcePermittedCompanies'
  | 'setResourceControlFields'
  | 'clearResourceControlFields'
  | 'setResourceWorkflowRestrictions'
  | 'clearResourceWorkflowRestrictions'
  | 'setResourceOperatingIntervals'
  | 'clearResourceOperatingIntervals'
  | 'scheduleLocationsValidation'
  | 'setMaterialResourceDataReady'
  | 'setEquipmentResourceDataReady'
>

interface ResourceRestrictionSource {
  readonly records: IResourceRecord[]
  readonly loading: boolean
  readonly error?: unknown
  readonly isReady: boolean
}

interface RegistrarProps {
  readonly store: ResourceRestrictionsStore
  readonly projectId?: string
  readonly equipment: ResourceRestrictionSource
  readonly materials: ResourceRestrictionSource
}

interface IProps {
  store: ResourceRestrictionsStore
  projectId?: string
}

/** Registers resource restrictions from already-owned source snapshots. */
export const ResourceRestrictionsRegistrar: React.FC<RegistrarProps> = ({
  store,
  projectId,
  equipment,
  materials,
}) => {
  const sources = [equipment, materials]

  // Memoized on the per-source record identities so the signature memo below
  // (and the effect) don't recompute on every render — flatMap would otherwise
  // build a fresh array each time and defeat the memo.
  const records: IResourceRecord[] = React.useMemo(
    () => [...equipment.records, ...materials.records],
    [equipment.records, materials.records],
  )
  // Register as soon as the FIRST source resolves rather than waiting for all,
  // so a fast resource type (e.g. equipment) isn't held pessimistically closed
  // while a slow one (e.g. a large materials dataset) is still loading. A
  // still-loading source contributes no records, so its ids are simply absent
  // from the maps until it resolves and re-registers. Equipment fails closed by
  // id via FRESH_RESOURCE_LOCATION_TYPES, but materials do NOT — so the store
  // gates submission on
  // isMaterialResourceDataReady (set below), keeping a permit that selects a
  // still-loading material fail-closed rather than passing it as unrestricted.
  const allInitiallyLoading = sources.every(source => source.loading)
  // Total failure: every source errored and produced nothing. A partial failure
  // (one type errors, another resolves) still registers the union — the failing
  // type's ids are simply absent from the maps and fail closed by id.
  const allFailed =
    sources.length > 0 &&
    sources.every(source => !!source.error && !source.records.length)

  // Re-register only when a field the effect below consumes changes, not every
  // render. Every consumed field must stay in the hash (incl. isDaily,
  // dayOfWeek and workflowRestrictions): a live query update (subscription/poll)
  // that touches only an omitted field would silently leave the store on stale
  // rules. dayOfWeek is the one that reads as redundant and is not — a
  // weekday-scoped window still carries isDaily true, so moving a schedule from
  // Monday to Tuesday at the same times changes nothing else about it.
  const signature = React.useMemo(
    () =>
      records
        .map(r => {
          const root = r.root ?? {}
          return [
            r.id,
            (root.permittedCompanies ?? []).join('|'),
            root.isBookable ?? false,
            (root.operatingIntervals ?? [])
              .map(
                i =>
                  `${i.startDate}-${i.endDate}-${i.isDaily ?? ''}-${
                    i.dayOfWeek ?? ''
                  }`,
              )
              .join('|'),
            JSON.stringify(root.workflowRestrictions ?? null),
          ].join(':')
        })
        .join(','),
    [records],
  )

  React.useEffect(() => {
    // Wait until at least one source has resolved. A missing-project source
    // reports loading:false, so guard on projectId too — otherwise
    // we'd register an empty map as "ready" and falsely fail closed for every
    // resource.
    if (allInitiallyLoading || !projectId) return
    // A wholesale failure (every type errored with empty records) would
    // register an empty map as "ready" and silently hard-block everything with
    // no error surface. Keep the gate honestly not-ready instead — a later
    // successful poll/refetch will register the real map. (An empty result with
    // no error is a genuinely resource-free project: register it.)
    if (allFailed) return
    store.setFreshResourcePermittedCompanies(
      buildResourcePermittedCompaniesMap(records),
    )
    store.setResourceControlFields(buildResourceControlFieldsMap(records))
    // Max-booking-duration / overlap and hours-of-operation are equipment-only
    // controls (materials keep only WR / Workflow Types / Permitted Companies),
    // so feed these maps from equipment records alone. Note this scopes the MAP,
    // not the rule: hours of operation apply to every location type, with the
    // other types reading theirs from the legacy attr instead (see
    // getHoursOfOperationWarning).
    store.setResourceWorkflowRestrictions(
      buildResourceWorkflowRestrictionsMap(equipment.records),
    )
    store.setResourceOperatingIntervals(
      buildResourceOperatingIntervalsMap(equipment.records),
    )
    // Re-validate now that the control maps are registered. The form's load-time
    // validation runs before these queries resolve, so without this the selected
    // resources' under-field warnings stay empty until the user's first edit
    // (the picker computes its reasons live, so it isn't affected).
    store.scheduleLocationsValidation()
    // `records` is captured via `signature` — depending on its identity would
    // re-register on every render.
  }, [signature, allInitiallyLoading, allFailed, projectId, store])

  // Mirror the shared settled-success readiness into the store's per-type
  // save-gates (see the NB above and isEquipmentResourceDataReady). A
  // still-loading / errored / no-project query stays not-ready (fail closed).
  React.useEffect(() => {
    store.setMaterialResourceDataReady(materials.isReady)
    store.setEquipmentResourceDataReady(equipment.isReady)
  }, [materials.isReady, equipment.isReady, store])

  // Clear on unmount AND on project switch: keying cleanup on projectId drops
  // the previous project's map before the new project's query resolves, so a
  // save in that window fails closed instead of reading cross-project data.
  React.useEffect(
    () => () => {
      store.clearFreshResourcePermittedCompanies()
      store.clearResourceControlFields()
      store.clearResourceWorkflowRestrictions()
      store.clearResourceOperatingIntervals()
      store.setMaterialResourceDataReady(false)
      store.setEquipmentResourceDataReady(false)
    },
    [projectId, store],
  )

  return null
}

// Form-scoped, render-nothing sync. The form outlives its picker modals, so it
// keeps both resource sources acquired while save validation can run.
const ResourceRestrictionsSync: React.FC<IProps> = observer(
  ({ store, projectId }) => {
    const equipment = useEquipmentResourceStore(projectId)
    const materials = useMaterialResourceStore(projectId)

    return (
      <ResourceRestrictionsRegistrar
        store={store}
        projectId={projectId}
        equipment={equipment}
        materials={materials}
      />
    )
  },
)

export default ResourceRestrictionsSync
